/**
 * Wire-контракт реестра фото и отправки (`Platform.Contracts/Photos`, `PhotoSendController`) и его проверка на границе: неожиданная форма —
 * `invalid-response`, а не пустой реестр. Адреса превью строит экран из `PHOTO_REGISTRY_URLS`, адресов из ответов нет.
 */
import { fail, flag, integer, list, optionalInteger, optionalText, record, text } from '../../../shared/decode'
import {
  JOB_STATES,
  type PhotoIdentity,
  type PhotoOccurrence,
  type PhotoPair,
  type PhotoRegistry,
  type PhotoSendResult,
  type PhotoSource,
  type PhotoSourceLimits,
  type PhotoSourceState,
  type PhotoStaging,
  type SendJob,
  type SendJobState,
} from '../model/types'

function decodeOccurrence(raw: unknown, name: string): PhotoOccurrence {
  const item = record(raw, name)
  return {
    resourceId: text(item.resourceId, `${name}.resourceId`),
    remoteProductKey: text(item.remoteProductKey, `${name}.remoteProductKey`),
    fileKey: text(item.fileKey, `${name}.fileKey`),
    sha256: text(item.sha256, `${name}.sha256`),
    matchMethod: text(item.matchMethod, `${name}.matchMethod`),
    isMainAtSource: flag(item.isMainAtSource, `${name}.isMainAtSource`),
    seenAt: text(item.seenAt, `${name}.seenAt`),
  }
}

function decodeIdentity(raw: unknown, name: string): PhotoIdentity {
  const item = record(raw, name)
  return {
    photoKey: text(item.photoKey, `${name}.photoKey`),
    token: text(item.token, `${name}.token`),
    previewUrl: text(item.previewUrl, `${name}.previewUrl`),
    occurrences: list(item.occurrences, `${name}.occurrences`, decodeOccurrence),
  }
}

function fraction(value: unknown, field: string): number | null {
  if (value === null || value === undefined) return null
  return typeof value === 'number' && Number.isFinite(value) ? value : fail(`поле ${field} — не число`)
}

export function decodePair(raw: unknown, name = 'pair'): PhotoPair {
  const item = record(raw, name)
  return {
    keyA: text(item.keyA, `${name}.keyA`),
    keyB: text(item.keyB, `${name}.keyB`),
    hashDistance: optionalInteger(item.hashDistance, `${name}.hashDistance`),
    cosine: fraction(item.cosine, `${name}.cosine`),
  }
}

function decodeSource(raw: unknown, name: string): PhotoSourceState {
  const item = record(raw, name)
  return {
    resourceId: text(item.resourceId, `${name}.resourceId`),
    unknown: flag(item.unknown, `${name}.unknown`),
    attemptedAt: text(item.attemptedAt, `${name}.attemptedAt`),
    readAt: optionalText(item.readAt, `${name}.readAt`),
    errorCode: optionalText(item.errorCode, `${name}.errorCode`),
    errorMessage: optionalText(item.errorMessage, `${name}.errorMessage`),
  }
}

/** `GET …/photo-registry` и ответ `POST …/decisions` (`PhotoRegistryDto`). */
export function decodeRegistry(raw: unknown): PhotoRegistry {
  const item = record(raw, 'photo-registry')
  return {
    productId: text(item.productId, 'photo-registry.productId'),
    photos: list(item.photos, 'photo-registry.photos', decodeIdentity),
    similar: list(item.similar, 'photo-registry.similar', decodePair),
    sources: list(item.sources, 'photo-registry.sources', decodeSource),
  }
}

/** `POST …/photos/staging` (`PhotoStagingDto`) и элемент списка `GET …/photos/staging`. */
export function decodeStaging(raw: unknown, name = 'photo-staging'): PhotoStaging {
  const item = record(raw, name)
  return {
    stagingId: text(item.stagingId, `${name}.stagingId`),
    productId: text(item.productId, `${name}.productId`),
    photoKey: text(item.photoKey, `${name}.photoKey`),
    token: text(item.token, `${name}.token`),
    expiresAt: optionalText(item.expiresAt, `${name}.expiresAt`),
    contentUrl: text(item.contentUrl, `${name}.contentUrl`),
  }
}

/** `GET …/photos/staging`: живые временные загрузки товара. */
export function decodeStagingList(raw: unknown): readonly PhotoStaging[] {
  return list(raw, 'photo-staging-list', decodeStaging)
}

/** `POST …/send` (`PhotoSendResultDto`): задача либо `outcome = unchanged` без неё. */
export function decodeSendResult(raw: unknown): PhotoSendResult {
  const item = record(raw, 'photo-send')
  return {
    jobId: optionalText(item.jobId, 'photo-send.jobId'),
    state: optionalText(item.state, 'photo-send.state'),
    outcome: text(item.outcome, 'photo-send.outcome'),
  }
}

function jobState(value: unknown): SendJobState {
  const known = JOB_STATES.find((state) => state === value)
  return known ?? fail('поле job.state — неизвестное состояние задачи')
}

/** `GET /api/sync/v1/apply-jobs/{id}`: нужны состояние задачи и строки с отказом источника; остальные поля задачи экрану не нужны. */
export function decodeSendJob(raw: unknown): SendJob {
  const item = record(raw, 'job')
  return {
    id: text(item.id, 'job.id'),
    state: jobState(item.state),
    errorMessage: optionalText(item.errorMessage, 'job.errorMessage'),
    items: list(item.items, 'job.items', (entry, name) => {
      const row = record(entry, name)
      return {
        state: text(row.state, `${name}.state`),
        errorCode: optionalText(row.errorCode, `${name}.errorCode`),
        message: optionalText(row.message, `${name}.message`),
        requestId: optionalText(row.requestId, `${name}.requestId`),
        targetResourceId: text(row.targetResourceId, `${name}.targetResourceId`),
      }
    }),
  }
}

function decodeSourceLimits(raw: unknown, name: string): PhotoSourceLimits | null {
  if (raw === null || raw === undefined) return null
  const item = record(raw, name)
  return {
    maxPhotos: optionalInteger(item.maxPhotos, `${name}.maxPhotos`),
    maxBytes: integer(item.maxBytes, `${name}.maxBytes`),
    note: text(item.note, `${name}.note`),
  }
}

function decodePhotoSource(raw: unknown, name: string): PhotoSource {
  const item = record(raw, name)
  const supports = record(item.supports, `${name}.supports`)
  const status = text(item.status, `${name}.status`)
  if (status !== 'ok' && status !== 'unknown') fail(`поле ${name}.status — неизвестное значение`)
  return {
    resourceId: text(item.resourceId, `${name}.resourceId`),
    kind: text(item.kind, `${name}.kind`),
    title: text(item.title, `${name}.title`),
    shortTitle: text(item.shortTitle, `${name}.shortTitle`),
    displayName: text(item.displayName, `${name}.displayName`),
    rank: integer(item.rank, `${name}.rank`),
    supports: {
      read: flag(supports.read, `${name}.supports.read`),
      add: flag(supports.add, `${name}.supports.add`),
      delete: flag(supports.delete, `${name}.supports.delete`),
    },
    limits: decodeSourceLimits(item.limits, `${name}.limits`),
    status,
    error: optionalText(item.error, `${name}.error`),
  }
}

/** `GET …/photo-sources` (`PhotoSourcesDto`): источники фото товара в порядке сервера (`rank`). */
export function decodePhotoSources(raw: unknown): readonly PhotoSource[] {
  const item = record(raw, 'photo-sources')
  return list(item.sources, 'photo-sources.sources', decodePhotoSource)
}
